import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Send, X, User, Stethoscope, Loader2, Plus, Building2, Phone, Mail, MapPin, IndianRupee } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Referral block — sits inside the consultation Wrap-up group. Renders
// behind a "Refer this patient" toggle so doctors don't see the picker
// unless they actually want to refer.
//
// Doctor sources (combined into one list, no dedupe needed — they live in
// different ID spaces):
//   1. /api/doctors                      — legacy external EMR doctors
//   2. /api/clinic-ops/referred-doctors  — new per-tenant address book
//                                          (created via the "+ Add new
//                                          doctor" inline form below)
//
// On select, if the referral is to a RESIDENT doctor we additionally hit
// POST /auto-appointments/resident-referral so the patient lands in the
// resident's queue ONLY after reception collects the fee.
export default function ReferralSection({ value, onChange, consultation }) {
  const initial = value && typeof value === 'object' ? value : null;
  const [enabled, setEnabled] = useState(!!initial?.referred_to_doctor_id);
  const [legacy, setLegacy] = useState([]);
  const [custom, setCustom] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [referredId, setReferredId] = useState(initial?.referred_to_doctor_id || '');
  const [remarks, setRemarks] = useState(initial?.remarks || '');
  const [showAddForm, setShowAddForm] = useState(false);

  const reloadCustom = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/clinic-ops/referred-doctors`, { withCredentials: true });
      setCustom(data || []);
    } catch (_) { setCustom([]); }
  }, []);

  useEffect(() => {
    if (!initial) return;
    setEnabled(!!initial.referred_to_doctor_id);
    setReferredId(initial.referred_to_doctor_id || '');
    setRemarks(initial.remarks || '');
  }, [initial?.referred_to_doctor_id, initial?.remarks]); // eslint-disable-line

  useEffect(() => {
    if (!enabled) return;
    setLoading(true);
    Promise.all([
      axios.get(`${API_URL}/api/doctors`, { withCredentials: true }).then(r => r.data).catch(() => []),
      axios.get(`${API_URL}/api/clinic-ops/referred-doctors`, { withCredentials: true }).then(r => r.data).catch(() => []),
    ]).then(([legacyRows, customRows]) => {
      setLegacy(legacyRows || []);
      setCustom(customRows || []);
    }).finally(() => setLoading(false));
  }, [enabled]);

  // Normalised combined list. Custom rows carry hospital/phone/email/kind
  // that legacy ones don't — UI shows whatever is available.
  const allDoctors = useMemo(() => {
    const a = legacy.map(d => ({
      id: d.id,
      source: 'legacy',
      name: d.name,
      specialization: d.specialization,
      external_doctor_id: d.external_doctor_id,
      kind: 'external',
      hospital: '', phone: '', email: '', address: '', resident_user_id: '',
    }));
    const b = (custom || []).map(d => ({
      id: d.id || d._id,
      source: 'custom',
      name: d.name,
      specialization: d.specialization || '',
      external_doctor_id: '',
      kind: d.kind || 'external',
      hospital: d.hospital || '',
      phone: d.phone || '',
      email: d.email || '',
      address: d.address || '',
      resident_user_id: d.resident_user_id || '',
      consult_fee: d.consult_fee || 0,
    }));
    return [...b, ...a]; // custom (tenant-owned) first
  }, [legacy, custom]);

  const filtered = useMemo(() => {
    if (!search.trim()) return allDoctors;
    const q = search.trim().toLowerCase();
    return allDoctors.filter(d =>
      d.name?.toLowerCase().includes(q) ||
      d.specialization?.toLowerCase().includes(q) ||
      d.hospital?.toLowerCase().includes(q) ||
      d.external_doctor_id?.toLowerCase().includes(q)
    );
  }, [allDoctors, search]);

  const selectedDoc = allDoctors.find(d => d.id === referredId);

  const persist = (overrides = {}) => {
    const next = {
      referred_to_doctor_id: overrides.id ?? referredId,
      referred_to_doctor_name: overrides.name ?? selectedDoc?.name ?? '',
      referred_to_specialization: overrides.spec ?? selectedDoc?.specialization ?? '',
      referred_to_external_id: overrides.ext ?? selectedDoc?.external_doctor_id ?? '',
      referred_to_hospital: overrides.hospital ?? selectedDoc?.hospital ?? '',
      referred_to_phone: overrides.phone ?? selectedDoc?.phone ?? '',
      referred_to_email: overrides.email ?? selectedDoc?.email ?? '',
      referred_to_address: overrides.address ?? selectedDoc?.address ?? '',
      referred_to_kind: overrides.kind ?? selectedDoc?.kind ?? 'external',
      remarks: overrides.remarks ?? remarks,
    };
    onChange(next.referred_to_doctor_id ? next : null);
  };

  const handleSelectDoc = async (doc) => {
    setReferredId(doc.id);
    setSearch('');
    persist({
      id: doc.id, name: doc.name, spec: doc.specialization,
      ext: doc.external_doctor_id, hospital: doc.hospital,
      phone: doc.phone, email: doc.email, address: doc.address,
      kind: doc.kind,
    });
    // If this is a RESIDENT referral and we have a patient → kick off the
    // reception-pending appointment. Failure is non-blocking so the
    // doctor can still save the referral as a note.
    if (doc.kind === 'resident' && doc.resident_user_id && consultation?.patient_id) {
      try {
        await axios.post(
          `${API_URL}/api/clinic-ops/auto-appointments/resident-referral`,
          {
            resident_user_id: doc.resident_user_id,
            patient_id: consultation.patient_id,
            consult_fee: doc.consult_fee || 0,
          },
          { withCredentials: true },
        );
      } catch (e) {
        console.warn('Auto-appointment for resident referral failed:', e);
      }
    }
  };

  const handleClear = () => {
    setEnabled(false); setReferredId(''); setRemarks(''); setSearch('');
    onChange(null);
  };

  if (!enabled) {
    return (
      <div className="px-1 py-2" data-testid="referral-section">
        <Button
          variant="outline"
          onClick={() => setEnabled(true)}
          className="gap-2 border-amber-300 text-amber-800 hover:bg-amber-50"
          data-testid="referral-enable-btn"
        >
          <Send className="h-4 w-4" />
          Refer this patient to another doctor
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="referral-section">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold text-amber-900">
          <Send className="h-4 w-4 text-amber-700" />
          Referral details
        </div>
        <Button
          variant="ghost" size="sm"
          onClick={handleClear}
          className="text-slate-500 hover:text-rose-600 gap-1"
          data-testid="referral-clear-btn"
        >
          <X className="h-3.5 w-3.5" /> Remove
        </Button>
      </div>

      {selectedDoc ? (
        <div
          className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 space-y-1.5"
          data-testid="referral-selected-doctor"
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-9 h-9 rounded-full bg-amber-200 flex items-center justify-center flex-shrink-0">
                <User className="h-4 w-4 text-amber-800" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-900 truncate">Dr. {selectedDoc.name}</p>
                <p className="text-xs text-slate-600 truncate flex items-center gap-1">
                  {selectedDoc.specialization && (<><Stethoscope className="h-3 w-3" />{selectedDoc.specialization}</>)}
                  {selectedDoc.kind === 'resident' && (
                    <span className="ml-1 px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-semibold text-[10px]">RESIDENT</span>
                  )}
                  {selectedDoc.kind === 'external' && selectedDoc.hospital && (
                    <span className="ml-1 px-1.5 py-0.5 rounded bg-sky-100 text-sky-700 font-semibold text-[10px]">{selectedDoc.hospital}</span>
                  )}
                </p>
              </div>
            </div>
            <Button variant="ghost" size="sm"
              onClick={() => { setReferredId(''); persist({ id: '', name: '', spec: '', ext: '' }); }}
              className="text-[11px] text-amber-700" data-testid="referral-change-doctor-btn">
              Change
            </Button>
          </div>
          {selectedDoc.kind === 'resident' && (
            <p className="text-[11px] text-emerald-700 mt-1 flex items-center gap-1">
              <IndianRupee className="h-3 w-3" />
              Reception will collect the consult fee before this lands in the resident&apos;s queue.
            </p>
          )}
        </div>
      ) : (
        <>
          <div className="flex gap-2">
            <Input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, speciality, or hospital…"
              className="h-9 text-sm flex-1" autoFocus
              data-testid="referral-search-input" />
            <Button variant="outline" size="sm"
              onClick={() => setShowAddForm(v => !v)}
              className="gap-1 border-amber-300 text-amber-800 hover:bg-amber-50"
              data-testid="referral-add-new-doctor-btn">
              <Plus className="h-3.5 w-3.5" /> New
            </Button>
          </div>
          {showAddForm && (
            <AddDoctorInlineForm
              onCancel={() => setShowAddForm(false)}
              onCreated={async (newDoc) => {
                await reloadCustom();
                setShowAddForm(false);
                // Auto-select the freshly added doctor.
                handleSelectDoc({
                  id: newDoc.id || newDoc._id,
                  source: 'custom',
                  name: newDoc.name,
                  specialization: newDoc.specialization,
                  external_doctor_id: '',
                  kind: newDoc.kind,
                  hospital: newDoc.hospital,
                  phone: newDoc.phone,
                  email: newDoc.email,
                  address: newDoc.address,
                  resident_user_id: newDoc.resident_user_id || '',
                  consult_fee: newDoc.consult_fee || 0,
                });
              }}
            />
          )}
          <div className="max-h-56 overflow-y-auto border border-slate-200 rounded-lg divide-y">
            {loading ? (
              <div className="p-4 flex items-center justify-center text-sm text-slate-500 gap-2">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading doctors…
              </div>
            ) : filtered.length === 0 ? (
              <div className="p-4 text-sm text-slate-500 italic text-center">
                {allDoctors.length === 0 ? 'No doctors on file yet — use "+ New" to add one.' : `No doctors match "${search}".`}
              </div>
            ) : (
              filtered.map((doc) => (
                <button key={`${doc.source}-${doc.id}`} type="button"
                  onClick={() => handleSelectDoc(doc)}
                  className="w-full text-left px-3 py-2 hover:bg-amber-50 transition-colors flex items-center gap-3"
                  data-testid={`referral-doc-option-${doc.id}`}>
                  <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                    <User className="h-4 w-4 text-slate-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-900 truncate flex items-center gap-1.5">
                      Dr. {doc.name}
                      {doc.kind === 'resident' && (
                        <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-semibold text-[10px]">RESIDENT</span>
                      )}
                      {doc.source === 'custom' && doc.kind !== 'resident' && (
                        <span className="px-1.5 py-0.5 rounded bg-sky-100 text-sky-700 font-semibold text-[10px]">EXTERNAL</span>
                      )}
                    </p>
                    <p className="text-xs text-slate-500 truncate">
                      {doc.specialization || 'General'}
                      {doc.hospital && <span className="ml-1">· {doc.hospital}</span>}
                      {doc.external_doctor_id && <span className="font-mono ml-1">· {doc.external_doctor_id}</span>}
                    </p>
                  </div>
                </button>
              ))
            )}
          </div>
        </>
      )}

      <div>
        <label className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-1 block">
          Remarks (optional)
        </label>
        <Textarea
          value={remarks} onChange={(e) => setRemarks(e.target.value)}
          onBlur={() => persist({ remarks })}
          placeholder="Reason for referral, urgency, what you've already ruled out, etc."
          rows={3} className="text-sm"
          data-testid="referral-remarks-input"
        />
      </div>
    </div>
  );
}


// ---------------- inline "+ Add new doctor" form -----------------
// Used when the doctor wants to send the patient to someone not in
// the existing address book. Saves to /api/clinic-ops/referred-doctors
// so the new doctor is available for future referrals across the
// whole tenant.
function AddDoctorInlineForm({ onCancel, onCreated }) {
  const [form, setForm] = useState({
    name: '', specialization: '', hospital: '', address: '',
    phone: '', email: '', kind: 'external',
    consult_fee: '', resident_user_id: '',
  });
  const [tenantUsers, setTenantUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Resident option: load the tenant's existing doctor users so the
  // creator can pick from them (sets resident_user_id automatically).
  useEffect(() => {
    if (form.kind !== 'resident' || tenantUsers.length > 0) return;
    axios.get(`${API_URL}/api/doctors`, { withCredentials: true })
      .then(r => setTenantUsers(r.data || []))
      .catch(() => {});
  }, [form.kind, tenantUsers.length]);

  const update = (k, v) => setForm(s => ({ ...s, [k]: v }));

  const submit = async () => {
    setError('');
    if (form.name.trim().length < 2) { setError('Doctor name is required'); return; }
    if (form.kind === 'resident' && !form.resident_user_id) {
      setError('Please pick which resident this points to'); return;
    }
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/clinic-ops/referred-doctors`,
        {
          ...form,
          consult_fee: parseFloat(form.consult_fee) || 0,
        },
        { withCredentials: true },
      );
      onCreated(data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not save doctor');
    } finally { setSaving(false); }
  };

  return (
    <div className="border border-amber-300 bg-amber-50/60 rounded-lg p-3 space-y-2.5"
         data-testid="referral-add-doctor-form">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-amber-900 flex items-center gap-1.5">
          <Plus className="h-4 w-4" /> Add new referral doctor
        </h4>
        <button type="button" onClick={onCancel}
          className="text-slate-400 hover:text-rose-500"
          data-testid="referral-add-doctor-close-btn">
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="flex gap-2 text-xs font-semibold">
        {['external', 'resident'].map(k => (
          <button key={k} type="button"
            onClick={() => update('kind', k)}
            className={`px-2.5 py-1 rounded-full border transition-colors ${
              form.kind === k
                ? (k === 'resident' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-sky-600 text-white border-sky-600')
                : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
            }`}
            data-testid={`referral-kind-${k}`}>
            {k === 'resident' ? '🏥 Resident (this clinic)' : '🩺 External doctor'}
          </button>
        ))}
      </div>

      {form.kind === 'resident' && (
        <div>
          <label className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-1 block">
            Which resident?
          </label>
          <select
            value={form.resident_user_id}
            onChange={(e) => {
              const u = tenantUsers.find(x => x.id === e.target.value);
              update('resident_user_id', e.target.value);
              if (u) {
                update('name', u.name || form.name);
                update('specialization', u.specialization || form.specialization);
              }
            }}
            className="w-full h-9 text-sm border border-slate-300 rounded-md px-2 bg-white"
            data-testid="referral-resident-picker">
            <option value="">— Pick from clinic doctors —</option>
            {tenantUsers.map(u => (
              <option key={u.id} value={u.id}>
                Dr. {u.name} {u.specialization ? `(${u.specialization})` : ''}
              </option>
            ))}
          </select>
        </div>
      )}

      <Field label="Doctor name *" icon={User}>
        <Input value={form.name} onChange={e => update('name', e.target.value)}
          placeholder="Full name" className="h-9 text-sm"
          data-testid="referral-form-name" />
      </Field>
      <div className="grid grid-cols-2 gap-2">
        <Field label="Specialisation" icon={Stethoscope}>
          <Input value={form.specialization} onChange={e => update('specialization', e.target.value)}
            placeholder="e.g. Cardiology" className="h-9 text-sm"
            data-testid="referral-form-spec" />
        </Field>
        <Field label="Consult fee (₹)" icon={IndianRupee}>
          <Input type="number" value={form.consult_fee}
            onChange={e => update('consult_fee', e.target.value)}
            placeholder="0" className="h-9 text-sm"
            data-testid="referral-form-fee" />
        </Field>
      </div>
      <Field label="Hospital / Clinic" icon={Building2}>
        <Input value={form.hospital} onChange={e => update('hospital', e.target.value)}
          placeholder={form.kind === 'resident' ? 'This clinic (auto)' : 'e.g. Apollo Hospital'}
          className="h-9 text-sm"
          disabled={form.kind === 'resident'}
          data-testid="referral-form-hospital" />
      </Field>
      <Field label="Address" icon={MapPin}>
        <Input value={form.address} onChange={e => update('address', e.target.value)}
          placeholder="Street, City, PIN" className="h-9 text-sm"
          data-testid="referral-form-address" />
      </Field>
      <div className="grid grid-cols-2 gap-2">
        <Field label="Phone" icon={Phone}>
          <Input value={form.phone} onChange={e => update('phone', e.target.value)}
            placeholder="+91 …" className="h-9 text-sm"
            data-testid="referral-form-phone" />
        </Field>
        <Field label="Email" icon={Mail}>
          <Input type="email" value={form.email} onChange={e => update('email', e.target.value)}
            placeholder="doctor@…" className="h-9 text-sm"
            data-testid="referral-form-email" />
        </Field>
      </div>

      {error && (
        <p className="text-xs text-rose-600 font-medium" data-testid="referral-form-error">
          {error}
        </p>
      )}
      <div className="flex justify-end gap-2 pt-1">
        <Button variant="outline" size="sm" onClick={onCancel}
          data-testid="referral-form-cancel">Cancel</Button>
        <Button size="sm" onClick={submit} disabled={saving}
          className="bg-amber-600 hover:bg-amber-700 text-white"
          data-testid="referral-form-save">
          {saving ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> Saving…</> : 'Save doctor'}
        </Button>
      </div>
    </div>
  );
}

function Field({ label, icon: Icon, children }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-1 flex items-center gap-1">
        {Icon && <Icon className="h-3 w-3" />} {label}
      </label>
      {children}
    </div>
  );
}
